import Vehicle from '@FreeRider/Player/Vehicles/Vehicle';
import BaseVehicle from "@FreeRider/Player/Vehicles/BaseVehicle";
import Mass from '@FreeRider/Physics/Mass';
import Wheel from '@FreeRider/Physics/Wheel';
import Spring from '@FreeRider/Physics/Spring';
import Track from '@FreeRider/Track';
import Gamepad from '@FreeRider/Utils/Gamepad';
import Ragdoll, { Stickman } from '@FreeRider/Player/Vehicles/Ragdoll';

/**
 * Mass Positions
 */
export enum MountainBikeMassesMap
{
    HEAD = 0,
    REAR_WHEEL = 1,
    FRONT_WHEEL = 2,
}

/**
 * Spring Positions
 */
export enum MountainBikeSpringsMap
{
    REAR_SPRING = 0,
    CHASSE = 1,
    FRONT_SPRING = 2,
}

/**
 * Mountain Bikes
 */
class MountainBike extends BaseVehicle implements Vehicle
{
    public masses: Mass[];
    public springs: Spring[];
    public pedala: number;
    public dir: number;
    public ragdoll: Ragdoll;

    /**
     * Constructor
     */
    constructor(player)
    {
        super(player);

        this.pedala = 0;
        this.dir = 1;
        this.createMasses();
        this.createSprings();
        this.ragdoll = new Ragdoll(player);
        this.reset();
    }

    /**
     * Returns the head mass
     */
    public getHead(): Mass
    {
        return this.masses[MountainBikeMassesMap.HEAD];
    }

    /**
     * Returns the real wheel mass
     */
    public getRearWheel(): Wheel
    {
        return this.masses[MountainBikeMassesMap.REAR_WHEEL] as Wheel;
    }

    /**
     * Returns the front wheel mass
     */
    public getFrontWheel(): Wheel
    {
        return this.masses[MountainBikeMassesMap.FRONT_WHEEL] as Wheel;
    }

    /**
     * Sets Defaults
     */
    public reset()
    {
        const masses = this.masses;

        const position = { x: 0, y: 35 };
        const velocity = { x: 0, y: 0 };

        const head = masses[MountainBikeMassesMap.HEAD];
        const frontWheel = masses[MountainBikeMassesMap.FRONT_WHEEL];
        const rearWheel = masses[MountainBikeMassesMap.REAR_WHEEL];

        head.reset();
        head.setPosition({ x: position.x + 2, y: position.y - 38 });
        head.setVelocity(velocity);

        frontWheel.reset();
        frontWheel.setPosition({ x: position.x + 23, y: position.y });
        frontWheel.setVelocity(velocity);

        rearWheel.reset();
        rearWheel.setPosition({ x: position.x - 23, y: position.y });
        rearWheel.setVelocity(velocity);

        this.pedala = 0;
        this.slow = false;

        this.trick.reset();
    }

    /**
     * Creates masses
     */
    private createMasses()
    {
        const masses = [];

        const head = new Mass(this);
        head.radius = 14;
        head.drive = this.dead.bind(this);

        const rearWheel = new Wheel(this);
        rearWheel.radius = 14;

        const frontWheel = new Wheel(this);
        frontWheel.radius = 14;

        masses.push(head);
        masses.push(rearWheel);
        masses.push(frontWheel);

        this.masses = masses;
    }


    /**
     * Creates springs
     */
    private createSprings()
    {
        const springs = [];
        const masses = this.masses;

        const head = masses[MountainBikeMassesMap.HEAD];
        const rearWheel = masses[MountainBikeMassesMap.REAR_WHEEL]
        const frontWheel = masses[MountainBikeMassesMap.FRONT_WHEEL];

        var rearSpring = new Spring(head, rearWheel, this);
        var chasse = new Spring(rearWheel, frontWheel, this);
        var frontSpring = new Spring(frontWheel, head, this);

        chasse.lrest = 45;
        chasse.leff = 45;
        chasse.spring_constant = 0.2;
        chasse.damp_constant = 0.3;

        rearSpring.lrest = 47;
        rearSpring.leff = 47;
        rearSpring.spring_constant = 0.2;
        rearSpring.damp_constant = 0.3;

        frontSpring.lrest = 45;
        frontSpring.leff = 45;
        frontSpring.spring_constant = 0.2;
        frontSpring.damp_constant = 0.3;

        springs.push(rearSpring);
        springs.push(chasse);
        springs.push(frontSpring);

        this.springs = springs;
    }


    /**
     * Stops control of bike
     */
    private dead()
    {
        if (this.player.isAlive)
        {
            const masses = this.masses;
            const head = masses[MountainBikeMassesMap.HEAD];
            const frontWheel = masses[MountainBikeMassesMap.FRONT_WHEEL] as Wheel;
            const rearWheel = masses[MountainBikeMassesMap.REAR_WHEEL] as Wheel;

            head.collide = false;
            rearWheel.motor = 0;
            rearWheel.brake = false;
            rearWheel.brake = false;
            this.slow = true;
            this.ragdoll.reset(this.getStickman());
            this.player.dead();
        }
    }

    /**
     * Returns a stickman object for ragdoll positioning
     */
    private getStickman(): Stickman
    {
        const masses = this.masses

        var dir = this.dir;

        const head = masses[MountainBikeMassesMap.HEAD];
        const frontWheel = masses[MountainBikeMassesMap.FRONT_WHEEL] as Wheel;
        const rearWheel = masses[MountainBikeMassesMap.REAR_WHEEL] as Wheel;
        const pedala = this.pedala;

        var head_position = head.position;
        var front_wheel_position = frontWheel.position;
        var rear_wheel_position = rearWheel.position;

        const p_x = front_wheel_position.x - rear_wheel_position.x;
        const p_y = front_wheel_position.y - rear_wheel_position.y;

        var n1_x = (head_position.x - ((front_wheel_position.x + rear_wheel_position.x) * 0.5));
        var n1_y = (head_position.y - ((front_wheel_position.y + rear_wheel_position.y) * 0.5));

        var n2_x = p_y * dir;
        var n2_y = -p_x * dir;

        var stick: Stickman = {
            head_x: 0,
            head_y: 0,
            left_hand_x: 0,
            left_hand_y: 0,
            right_hand_x: 0,
            right_hand_y: 0,
            left_elbow_x: 0,
            left_elbow_y: 0,
            right_elbow_x: 0,
            right_elbow_y: 0,
            waist_x: 0,
            waist_y: 0,
            left_foot_x: 0,
            left_foot_y: 0,
            right_foot_x: 0,
            right_foot_y: 0,
            left_knee_x: 0,
            left_knee_y: 0,
            right_knee_x: 0,
            right_knee_y: 0,
            head_vel_x: 0,
            head_vel_y: 0,
            feet_vel_x: 0,
            feet_vel_y: 0,
            dir: 0
        };

        stick.head_x = (rear_wheel_position.x + (p_x * 0.35)) + (n1_x * 1.2);
        stick.head_y = (rear_wheel_position.y + (p_y * 0.35)) + (n1_y * 1.2);

        stick.left_hand_x = stick.right_hand_x = (rear_wheel_position.x + (p_x * 0.8)) + (n2_x * 0.68);
        stick.left_hand_y = stick.right_hand_y = (rear_wheel_position.y + (p_y * 0.8)) + (n2_y * 0.68);

        var e_x = stick.head_x - stick.left_hand_x;
        var e_y = stick.head_y - stick.left_hand_y;

        var e_x_calc = e_y * dir;
        var e_y_calc = -e_x * dir;

        e_x = e_x_calc;
        e_y = e_y_calc;

        var e_lenSqr = Math.pow(e_x, 2) + Math.pow(e_y, 2);

        stick.left_elbow_x = stick.right_elbow_x = ((stick.head_x + stick.left_hand_x) * 0.5) + (e_x * (130 / e_lenSqr));
        stick.left_elbow_y = stick.right_elbow_y = ((stick.head_y + stick.left_hand_y) * 0.5) + (e_y * (130 / e_lenSqr));

        stick.waist_x = (rear_wheel_position.x + (p_x * 0.2)) + (n2_x * 0.5);
        stick.waist_y = (rear_wheel_position.y + (p_y * 0.2)) + (n2_y * 0.5);

        var off_x = 6 * Math.cos(pedala);
        var off_y = 6 * Math.sin(pedala);

        stick.left_foot_x = (rear_wheel_position.x + (p_x * 0.4)) + (n2_x * 0.05) + off_x;
        stick.left_foot_y = (rear_wheel_position.y + (p_y * 0.4)) + (n2_y * 0.05) + off_y;

        e_x = stick.waist_x - stick.left_foot_x;
        e_y = stick.waist_y - stick.left_foot_y;

        e_x_calc = e_y * dir;
        e_y_calc = -e_x * dir;

        e_x = e_x_calc;
        e_y = e_y_calc;

        e_lenSqr = Math.pow(e_x, 2) + Math.pow(e_y, 2);

        stick.left_knee_x = ((stick.waist_x + stick.left_foot_x) * 0.5) + (e_x * (160 / e_lenSqr));
        stick.left_knee_y = ((stick.waist_y + stick.left_foot_y) * 0.5) + (e_y * (160 / e_lenSqr));

        stick.right_foot_x = (rear_wheel_position.x + (p_x * 0.4)) + (n2_x * 0.05) - off_x;
        stick.right_foot_y = (rear_wheel_position.y + (p_y * 0.4)) + (n2_y * 0.05) - off_y;

        e_x = stick.waist_x - stick.right_foot_x;
        e_y = stick.waist_y - stick.right_foot_y;

        e_x_calc = e_y * dir;
        e_y_calc = -e_x * dir;

        e_x = e_x_calc;
        e_y = e_y_calc;

        e_lenSqr = Math.pow(e_x, 2) + Math.pow(e_y, 2);

        stick.right_knee_x = ((stick.waist_x + stick.right_foot_x) * 0.5) + (e_x * (160 / e_lenSqr));
        stick.right_knee_y = ((stick.waist_y + stick.right_foot_y) * 0.5) + (e_y * (160 / e_lenSqr));

        stick.head_vel_x = head.vel.x;

        stick.head_vel_y = head.vel.y;

        stick.feet_vel_x = rearWheel.vel.x;

        stick.feet_vel_y = rearWheel.vel.y;

        stick.dir = dir;

        return stick;
    }


    /**
     * Updates the mountain bike physics
     * @param track - track to update against
     */
    public update(track: Track)
    {
        const player = this.player;
        const masses = this.masses;
        const springs = this.springs;

        const rearWheel = masses[MountainBikeMassesMap.REAR_WHEEL] as Wheel;
        const frontWheel = masses[MountainBikeMassesMap.FRONT_WHEEL] as Wheel;

        if (player.isAlive)
        {
            this.control();
        }

        const springCount = springs.length;
        for (let s = springCount - 1; s >= 0; s--)
        {
            springs[s].update();
        }

        const massCount = masses.length;
        for (let m = massCount - 1; m >= 0; m--)
        {
            masses[m].update(track);
        }


        if (this.slow === false)
        {
            //If player is alive, allow control
            if (player.isAlive)
            {
                this.control();
            }

            //Update Springs
            for (let s = springCount - 1; s >= 0; s--)
            {
                springs[s].update();
            }

            //Update Masses
            for (let m = massCount - 1; m >= 0; m--)
            {
                masses[m].update(track);
            }
        }

        if (this.player.isAlive === false)
        {
            this.ragdoll.update(track);
        }

        if (this.player.isAlive)
        {
            this.trick.update(frontWheel, rearWheel, this.dir);
        }

        if (rearWheel.contact && frontWheel.contact)
        {
            this.stable = true;
        }
        else
        {
            this.stable = false;
        }
    }

    /**
     * Controls the vehicle based on which controls are down
     */
    private control()
    {
        const gamepad = this.player.gamepad;

        const accelerate = gamepad.isButtonDown(Gamepad.KEYS.ACCELERATE);
        const brake = gamepad.isButtonDown(Gamepad.KEYS.BRAKE);
        const left = gamepad.isButtonDown(Gamepad.KEYS.LEAN_LEFT);
        const right = gamepad.isButtonDown(Gamepad.KEYS.LEAN_RIGHT);

        const a = accelerate ? 1 : 0;

        const masses = this.masses;
        const springs = this.springs;
        const rearWheel = masses[MountainBikeMassesMap.REAR_WHEEL] as Wheel;
        const rearSpring = springs[MountainBikeSpringsMap.REAR_SPRING];
        const frontSpring = springs[MountainBikeSpringsMap.FRONT_SPRING];
        const chasse = springs[MountainBikeSpringsMap.CHASSE];

        rearWheel.motor += (a - rearWheel.motor) / 10;

        if (accelerate)
        {
            this.pedala += rearWheel.speed / 5;
        }

        var l = left ? 1 : 0;
        l += right ? -1 : 0;

        rearSpring.contract(l * 5 * this.dir, 5);
        frontSpring.contract(-l * 5 * this.dir, 5);
        chasse.rotate(l / 8);

        if (!l && accelerate)
        {
            rearSpring.contract(-7, 5);
            frontSpring.contract(7, 5);
        }
    }

    /**
     * Returns the focual point mass
     * This is used by the camera to know which mass to follow
     */
    public getFocalPoint()
    {
        let focalPoint: Mass;

        if (this.player.isAlive === false)
        {
            focalPoint = this.ragdoll.getFocalPoint();
        }
        else
        {
            focalPoint = this.masses[MountainBikeMassesMap.HEAD];
        }

        return focalPoint
    }
}

export default MountainBike;